import React, { useEffect } from "react";
import { motion } from "framer-motion";
import { X } from "lucide-react";

// Helper — extract YouTube video id from a youtu.be / watch / embed URL
export const ytId = (url) => {
  if (!url) return "";
  const m = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/))([\w-]{11})/);
  return m ? m[1] : "";
};

/**
 * Cinematic YouTube modal — used on Work page and Home page.
 * Expects a `project` object with `{ title, year, category, youtube, summary }`.
 */
const YouTubeModal = ({ project, onClose }) => {
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [onClose]);

  if (!project) return null;
  const id = ytId(project.youtube);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.35 }}
      className="fixed inset-0 z-[90] bg-ink/95 backdrop-blur-xl flex items-center justify-center p-4 md:p-10"
      onClick={onClose}
      data-testid="yt-modal"
    >
      <button
        className="absolute top-5 right-5 md:top-8 md:right-8 w-12 h-12 inline-flex items-center justify-center border border-bone/20 hover:border-gold hover:bg-gold hover:text-ink text-bone transition-all duration-300 z-10"
        onClick={onClose}
        aria-label="Close video"
        data-testid="yt-modal-close"
      >
        <X size={20} />
      </button>

      <div
        className="relative w-full max-w-6xl"
        onClick={(e) => e.stopPropagation()}
      >
        {(project.category || project.year) && (
          <div className="overline mb-4 text-gold/85">
            {[project.category, project.year].filter(Boolean).join(" · ")}
          </div>
        )}
        <h3
          className="font-serif text-bone leading-[1.05] tracking-tight mb-6"
          style={{ fontSize: "clamp(1.6rem, 3.2vw, 2.6rem)" }}
        >
          {project.title}
        </h3>

        <div className="relative w-full bg-black border border-gold/30" style={{ aspectRatio: "16/9" }}>
          <iframe
            className="absolute inset-0 w-full h-full"
            src={`https://www.youtube.com/embed/${id}?autoplay=1&rel=0`}
            title={project.title}
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            data-testid="yt-modal-iframe"
          />
        </div>

        {project.summary && (
          <p className="mt-5 text-bone/65 font-sans text-sm md:text-base max-w-3xl leading-relaxed">
            {project.summary}
          </p>
        )}
      </div>
    </motion.div>
  );
};

export default YouTubeModal;
